/**
 * AuthorizationContext — dinamik permission + hierarchy chain bilgisi.
 *
 * AuthContext.jsx üzerinde HİÇ değişiklik yapılmadan, dış sarmal olarak
 * permission ve chain bilgisini context'e taşır. Tüm component'ler
 * useAuthorization() ile aynı cache'ten okur (her component'in ayrı fetch
 * yapmasının önüne geçer).
 *
 * Sözleşme (Sprint 2 frontend uygulaması):
 *   const {
 *     permissions, role, role_id, user_id,
 *     has, hasAny, hasAll,
 *     parentChain, parentIds, supervisor,
 *     subordinateIds, subordinateSet, isInChain,
 *     loading, error, refresh
 *   } = useAuthorization();
 *
 * Migration helper (eski kodla uyum):
 *   const { userRole, personnelId } = useAuth();  // hâlâ çalışır
 *
 * SSE (Sprint 2.5+): "permission_updated" event geldiğinde refresh() çağrılır.
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";

import { useAuth } from "./AuthContext";
import { useSSE } from "../hooks/useSSE";
import apiClient from "../api/client";


const AuthorizationContext = createContext(null);


// ---------------------------------------------------------------------------
// In-memory cache (sayfa refresh'ine kadar dayanır; localStorage backup'lı)
// ---------------------------------------------------------------------------


const PERM_CACHE_KEY = "belvizyon_authz_permissions_v1";
const CHAIN_CACHE_KEY = "belvizyon_authz_chain_v1";
const SUBS_CACHE_KEY = "belvizyon_authz_subs_v1";


function readCache(key, storage) {
  try {
    const raw = (storage || localStorage).getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}


function writeCache(key, data, storage) {
  try {
    (storage || localStorage).setItem(key, JSON.stringify(data));
  } catch {
    /* quota dolu olabilir — sessiz geç */
  }
}


function clearCaches() {
  try {
    localStorage.removeItem(PERM_CACHE_KEY);
    sessionStorage.removeItem(CHAIN_CACHE_KEY);
    sessionStorage.removeItem(SUBS_CACHE_KEY);
  } catch {
    /* ignore */
  }
}


// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------


export function AuthorizationProvider({ children }) {
  const auth = useAuth();
  const isLoggedIn = auth.isLoggedIn;
  const personnelId = auth.user?.personnelId || "";
  const userRoleStr = auth.user?.userRole || "";

  // 3 ayrı state — paralel fetch
  const [permData, setPermData] = useState(() => readCache(PERM_CACHE_KEY, localStorage));
  const [chainData, setChainData] = useState(() => readCache(CHAIN_CACHE_KEY, sessionStorage));
  const [subsData, setSubsData] = useState(() => readCache(SUBS_CACHE_KEY, sessionStorage));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ---------- fetch ----------

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await apiClient.get("/me/permissions");
      const payload = res.data || {};
      const next = {
        user_id: payload.user_id || null,
        role: payload.role || null,
        role_id: payload.role_id || null,
        permissions: Array.isArray(payload.permissions) ? payload.permissions : [],
      };
      setPermData(next);
      writeCache(PERM_CACHE_KEY, next, localStorage);
      return next;
    } catch (err) {
      if (err?.response?.status === 401) {
        clearCaches();
        setPermData(null);
      }
      throw err;
    }
  }, []);

  const fetchChain = useCallback(async () => {
    try {
      const res = await apiClient.get("/organizasyon/me/chain");
      const payload = res.data || {};
      const next = {
        chain: Array.isArray(payload.chain) ? payload.chain : [],
        count: payload.count || 0,
      };
      setChainData(next);
      writeCache(CHAIN_CACHE_KEY, next, sessionStorage);
      return next;
    } catch (err) {
      // 403 olmaz; bu endpoint sadece auth gerektirir
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(CHAIN_CACHE_KEY);
        setChainData(null);
      }
      // Sessizce kabul — chain bilgisiz UI yine de render olur
      return null;
    }
  }, []);

  const fetchSubordinates = useCallback(async () => {
    try {
      const res = await apiClient.get("/organizasyon/me/subordinates");
      const payload = res.data || {};
      const next = {
        personnel_id: payload.personnel_id || null,
        subordinate_ids: Array.isArray(payload.subordinate_ids) ? payload.subordinate_ids : [],
        count: payload.count || 0,
      };
      setSubsData(next);
      writeCache(SUBS_CACHE_KEY, next, sessionStorage);
      return next;
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(SUBS_CACHE_KEY);
        setSubsData(null);
      }
      if (err?.response?.status === 403) {
        // organizasyon.agac_gor yetkisi yok — park görevlisi vb
        const empty = { personnel_id: personnelId, subordinate_ids: [], count: 0 };
        setSubsData(empty);
        writeCache(SUBS_CACHE_KEY, empty, sessionStorage);
        return empty;
      }
      return null;
    }
  }, [personnelId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchPermissions(), fetchChain(), fetchSubordinates()]);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [fetchPermissions, fetchChain, fetchSubordinates]);

  // ---------- lifecycle ----------

  // Login durumu değişince fetch et / temizle
  useEffect(() => {
    if (!isLoggedIn) {
      setPermData(null);
      setChainData(null);
      setSubsData(null);
      clearCaches();
      return;
    }
    // Login + personnelId hazırsa fetch
    if (personnelId) {
      refresh();
    }
  }, [isLoggedIn, personnelId, refresh]);

  // SSE: Admin paneli yetki değiştirdiğinde anlık refresh (Sprint 4 F2)
  // Backend `permission_updated` event'i bu role atanmış aktif kullanıcılara push'lar.
  // useSSE useTabLeader ile yalnız lider sekmede bağlantı açar; diğer sekmeler
  // localStorage broadcast'i üzerinden event'i alır.
  useSSE(
    isLoggedIn ? personnelId : null,
    useMemo(
      () => ({
        permission_updated: () => {
          // Permission cache'i invalidate et + yenile
          refresh();
          try {
            toast.info("Yetkilerin güncellendi", {
              duration: 3000,
              description: "Erişim haklarınız yeniden yüklendi.",
            });
          } catch {
            /* toast altyapısı henüz yüklenmemiş olabilir */
          }
        },
        // Operasyon emirleri olayları da bu (tek, kanıtlanmış çalışan) SSE
        // tüketicisinden window event'lerine köprülenir; OperasyonTab dinler.
        operation_assigned: (payload) => {
          try { toast.info(`Yeni görev: ${payload?.title || ""}`); } catch { /* noop */ }
          window.dispatchEvent(new CustomEvent("operation_assigned", { detail: payload || {} }));
        },
        operation_updated: (payload) => {
          window.dispatchEvent(new CustomEvent("operation_updated", { detail: payload || {} }));
        },
        operation_deleted: (payload) => {
          window.dispatchEvent(new CustomEvent("operation_deleted", { detail: payload || {} }));
        },
      }),
      [refresh],
    ),
  );

  // Robustluk: sekme tekrar GÖRÜNÜR olduğunda yetkileri yeniden çek.
  // SSE bağlantısı sekme arka plandayken/lider değilken kapanabiliyor ve o
  // sırada gelen `permission_updated` event'i kaçabiliyor (yeniden bağlanınca
  // tekrar oynatılmıyor). Bu efekt, kullanıcı sekmeye dönünce en güncel
  // yetkileri çekerek "eş zamanlı güncellenmedi" durumunu telafi eder.
  useEffect(() => {
    if (!isLoggedIn || !personnelId) return undefined;
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [isLoggedIn, personnelId, refresh]);

  // ---------- derived ----------

  const permissionSet = useMemo(
    () => new Set(permData?.permissions || []),
    [permData]
  );

  const has = useCallback(
    (slug) => permissionSet.has(slug),
    [permissionSet]
  );

  const hasAny = useCallback(
    (slugs) => Array.isArray(slugs) && slugs.some((s) => permissionSet.has(s)),
    [permissionSet]
  );

  const hasAll = useCallback(
    (slugs) => Array.isArray(slugs) && slugs.every((s) => permissionSet.has(s)),
    [permissionSet]
  );

  const parentIds = useMemo(() => {
    if (!chainData?.chain) return [];
    return chainData.chain.filter((n) => n.depth > 0).map((n) => n.personnel_id);
  }, [chainData]);

  const supervisor = useMemo(
    () => chainData?.chain?.find((n) => n.depth === 1) || null,
    [chainData]
  );

  const subordinateSet = useMemo(
    () => new Set(subsData?.subordinate_ids || []),
    [subsData]
  );

  const isInChain = useCallback(
    (targetId) => {
      if (!targetId) return false;
      if (targetId === personnelId) return true;
      return subordinateSet.has(targetId);
    },
    [subordinateSet, personnelId]
  );

  // ---------- value ----------

  const value = useMemo(
    () => ({
      // Auth re-export
      isLoggedIn,
      personnelId,
      personnelName: auth.user?.personnelName || "",
      userRole: userRoleStr,

      // Permission
      permissions: permData?.permissions || [],
      role: permData?.role || userRoleStr,
      role_id: permData?.role_id || null,
      user_id: permData?.user_id || personnelId,
      has,
      hasAny,
      hasAll,

      // Chain (üst)
      parentChain: chainData?.chain || [],
      parentIds,
      supervisor,

      // Subordinates (alt)
      subordinateIds: subsData?.subordinate_ids || [],
      subordinateSet,
      isInChain,

      // Lifecycle
      loading,
      error,
      refresh,
    }),
    [
      isLoggedIn,
      personnelId,
      auth.user?.personnelName,
      userRoleStr,
      permData,
      has,
      hasAny,
      hasAll,
      chainData,
      parentIds,
      supervisor,
      subsData,
      subordinateSet,
      isInChain,
      loading,
      error,
      refresh,
    ]
  );

  return (
    <AuthorizationContext.Provider value={value}>
      {children}
    </AuthorizationContext.Provider>
  );
}


// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------


export function useAuthorization() {
  const ctx = useContext(AuthorizationContext);
  if (!ctx) {
    throw new Error(
      "useAuthorization, <AuthorizationProvider> içinde kullanılmalıdır."
    );
  }
  return ctx;
}


// ---------------------------------------------------------------------------
// Kısayol hook'ları (yaygın kullanım için)
// ---------------------------------------------------------------------------


/**
 * Tek slug için izin kontrolü.
 *   const { allowed, loading } = useHasPermission('izin.onayla');
 */
export function useHasPermission(slug) {
  const { has, loading } = useAuthorization();
  return { allowed: has(slug), loading };
}


/**
 * Birden fazla slug — herhangi biri yeterli.
 *   const { allowed } = useHasAnyPermission(['izin.onayla', 'izin.son_onay_my']);
 */
export function useHasAnyPermission(slugs) {
  const { hasAny, loading } = useAuthorization();
  return { allowed: hasAny(slugs), loading };
}


/**
 * Hedef personel benim alt zincirimde mi?
 *   const { allowed } = useInChain(personnel.id);
 */
export function useInChain(targetPersonnelId) {
  const { isInChain, loading } = useAuthorization();
  return { allowed: isInChain(targetPersonnelId), loading };
}


export default AuthorizationContext;
